/**
 * Room "Fill In" wizard.
 *
 * Steps through every item in the room that's still missing one or more
 * of {budget, listed_price, ordered_price, status, receipt} and lets the
 * admin batch-fill the gaps with sensible suggestions (rules below). The
 * wizard saves each item exactly the same way the inline editor does
 * (`onUpdateItem`, `onUploadReceipt`) so changes round-trip identically.
 *
 * Suggestion rules (per the user's spec):
 *   1. Budget       — if missing & ordered_price is set → suggest ordered_price.
 *   2. Listed price — if missing → suggest ordered_price (fallback: budget).
 *   3. Ordered price — no auto-suggestion. Optional receipt + amount.
 *   4. Status       — if not "delivered" / "returned" → suggest "delivered".
 *   5. Receipt      — only nudge if the item has zero receipts attached.
 *
 * The wizard intentionally never *clears* an existing value — fields
 * already filled are not even shown. The user can skip any field or the
 * entire item, and "Save & exit" closes the wizard at any point with
 * whatever's already been saved persisted.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ChevronRight, Loader2, Paperclip, CheckCircle2, SkipForward, X, Save } from 'lucide-react';
import { toast } from 'sonner';

// Status options come from the inline editor; keep in sync.
const STATUS_OPTIONS = ['', 'pending', 'ordered', 'delivered', 'returned'];
const STATUS_FILLED = (s) => s === 'delivered' || s === 'returned';

// "Filled" definition — used to decide whether an item is in scope at all
// and which fields the wizard should ask about for the current item.
const fieldMissing = (item, field) => {
  if (field === 'budget') return item.budget == null || item.budget === '';
  if (field === 'listed_price') return item.listed_price == null || item.listed_price === '';
  if (field === 'ordered_price') return item.ordered_price == null || item.ordered_price === '';
  if (field === 'status') return !STATUS_FILLED(item.status || '');
  if (field === 'receipt') return false; // receipt missingness is computed from receiptsByItem outside
  return false;
};

const computeMissing = (item, hasReceipt) => {
  const missing = [];
  if (fieldMissing(item, 'budget')) missing.push('budget');
  if (fieldMissing(item, 'listed_price')) missing.push('listed_price');
  if (fieldMissing(item, 'ordered_price')) missing.push('ordered_price');
  if (fieldMissing(item, 'status')) missing.push('status');
  if (!hasReceipt) missing.push('receipt');
  return missing;
};

// Suggested defaults for the editable fields, per the spec.
const initialDraft = (item) => {
  const ordered = item.ordered_price;
  const budget = item.budget;
  return {
    budget: fieldMissing(item, 'budget')
      ? (ordered != null && ordered !== '' ? String(ordered) : '')
      : '',
    listed_price: fieldMissing(item, 'listed_price')
      ? (ordered != null && ordered !== '' ? String(ordered)
          : (budget != null && budget !== '' ? String(budget) : ''))
      : '',
    ordered_price: '', // no suggestion
    status: fieldMissing(item, 'status') ? 'delivered' : '',
    receipt_amount: '',
  };
};

export function FillInWizard({
  open,
  onClose,
  room,
  kind,                 // 'items' | 'essentials'
  receiptsByItem = {},  // { [itemId]: [receipt, ...] }
  onUpdateItem,         // (itemId, patch) => Promise
  onUploadReceipt,      // ({ file, itemIds, amount }) => Promise<receipt>
  onSaved,              // () => void   (called once after wizard closes, for parent refresh)
}) {
  // ── scope: which items are in this run, in stable order ──
  const targetItems = useMemo(() => {
    const all = room?.items || [];
    const isEssential = kind === 'essentials';
    return all
      .filter((i) => !!i.is_essential === isEssential)
      .map((it) => ({ ...it, _missing: computeMissing(it, (receiptsByItem[it.id] || []).length > 0) }))
      .filter((it) => it._missing.length > 0);
    // Recompute whenever the room or receipts map changes — covers the
    // optimistic update right after a save, so the *next* item in line
    // reflects what was just persisted.
  }, [room, kind, receiptsByItem]);

  const [idx, setIdx] = useState(0);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const fileRef = useRef(null);

  // Reset the wizard state every time it opens. Without this, closing &
  // reopening mid-list would keep the last item's draft and start at a
  // stale `idx` (potentially past the end of the new list).
  useEffect(() => {
    if (open) { setIdx(0); }
  }, [open]);
  // Re-seed the draft whenever the *current item* changes. We track by id
  // so optimistic field updates in the same item don't reset typing.
  const currentItem = targetItems[idx];
  const currentId = currentItem?.id;
  useEffect(() => {
    if (currentItem) setDraft(initialDraft(currentItem));
  }, [currentId]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  const total = targetItems.length;
  const done = total === 0 || idx >= total;
  const itemReceipts = currentItem ? (receiptsByItem[currentItem.id] || []) : [];

  // Build the PATCH payload from the draft. Only fields the user actually
  // populated get sent — leaving an input blank means "skip this field".
  const buildPatch = () => {
    if (!currentItem) return {};
    const patch = {};
    const missing = currentItem._missing;
    const tryNum = (v) => {
      if (v == null || v === '') return null;
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : null;
    };
    if (missing.includes('budget') && draft.budget !== '') {
      const n = tryNum(draft.budget);
      if (n != null) patch.budget = n;
    }
    if (missing.includes('listed_price') && draft.listed_price !== '') {
      const n = tryNum(draft.listed_price);
      if (n != null) patch.listed_price = n;
    }
    if (missing.includes('ordered_price') && draft.ordered_price !== '') {
      const n = tryNum(draft.ordered_price);
      if (n != null) patch.ordered_price = n;
    }
    if (missing.includes('status') && draft.status && draft.status !== '') {
      patch.status = draft.status;
    }
    return patch;
  };

  const handleSaveItem = async ({ andAdvance }) => {
    if (!currentItem) return;
    const patch = buildPatch();
    setSaving(true);
    try {
      if (Object.keys(patch).length > 0) {
        await onUpdateItem(currentItem.id, patch);
      }
      if (andAdvance) {
        // If this was the last item, finish; otherwise advance.
        if (idx + 1 >= total) {
          onSaved && onSaved();
          onClose();
          toast.success('Fill in complete');
        } else {
          setIdx((i) => i + 1);
        }
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleSkipItem = () => {
    if (idx + 1 >= total) {
      onSaved && onSaved();
      onClose();
    } else {
      setIdx((i) => i + 1);
    }
  };

  const handleSaveAndExit = async () => {
    await handleSaveItem({ andAdvance: false });
    onSaved && onSaved();
    onClose();
  };

  const handleReceiptFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !currentItem) return;
    setUploadingReceipt(true);
    try {
      // Pull whatever the user typed for ordered_price as the receipt
      // amount fallback — saves a separate input when both will likely
      // match. If they typed a different `receipt_amount` use that.
      const amount = draft.receipt_amount !== ''
        ? draft.receipt_amount
        : (draft.ordered_price !== '' ? draft.ordered_price : '');
      await onUploadReceipt({ file, itemIds: [currentItem.id], amount });
      toast.success('Receipt attached');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to attach receipt');
    } finally {
      setUploadingReceipt(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const isLast = idx + 1 >= total;
  const fmtCurrency = (v) => v == null || v === '' ? '—' : `$${Number(v).toLocaleString()}`;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg p-0 overflow-hidden flex flex-col" data-testid="fill-in-wizard">
        <DialogHeader className="px-5 pt-5 pb-3 border-b bg-muted/30">
          <DialogTitle className="text-base flex items-center gap-2">
            Fill in {kind === 'essentials' ? 'essentials' : 'items'} —{' '}
            <span className="font-normal text-muted-foreground">{room?.name}</span>
          </DialogTitle>
          <DialogDescription className="text-xs">
            {total === 0
              ? 'Everything in this section is already filled in. 🎉'
              : `${total} item${total === 1 ? '' : 's'} with missing info · viewing item ${Math.min(idx + 1, total)} of ${total}`}
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="px-6 py-10 flex flex-col items-center gap-3 text-center">
            <CheckCircle2 className="h-10 w-10 text-emerald-600" />
            <p className="text-sm font-medium">All caught up</p>
            <p className="text-xs text-muted-foreground">No items in this section are missing budget, prices, status, or a receipt.</p>
            <Button onClick={onClose} size="sm" className="mt-2" data-testid="fillin-done-btn">Done</Button>
          </div>
        ) : (
          <>
            <div className="px-5 py-4 space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-[#1f2933]" data-testid="fillin-current-name">{currentItem.name || 'Untitled item'}</p>
                  <p className="text-[11px] text-muted-foreground">
                    Current — Budget {fmtCurrency(currentItem.budget)} · Listed {fmtCurrency(currentItem.listed_price)} ·
                    Ordered {fmtCurrency(currentItem.ordered_price)} · Status {currentItem.status || '—'}
                  </p>
                </div>
                <Badge variant="outline" className="text-[10px]">
                  {currentItem._missing.length} field{currentItem._missing.length === 1 ? '' : 's'} missing
                </Badge>
              </div>

              {/* ── Budget ── */}
              {currentItem._missing.includes('budget') && (
                <FieldRow
                  label="Budget"
                  testIdBase="fillin-budget"
                  hint={currentItem.ordered_price != null && currentItem.ordered_price !== ''
                    ? `Suggested: same as Ordered Price ($${Number(currentItem.ordered_price).toLocaleString()}). Adjust if needed.`
                    : 'No suggestion — enter a number or skip.'}
                  value={draft.budget}
                  onChange={(v) => setDraft((d) => ({ ...d, budget: v }))}
                  type="number"
                />
              )}

              {/* ── Listed Price ── */}
              {currentItem._missing.includes('listed_price') && (
                <FieldRow
                  label="Listed price"
                  testIdBase="fillin-listed"
                  hint={(() => {
                    if (currentItem.ordered_price != null && currentItem.ordered_price !== '')
                      return `Suggested: same as Ordered Price ($${Number(currentItem.ordered_price).toLocaleString()}).`;
                    if (currentItem.budget != null && currentItem.budget !== '')
                      return `Suggested: same as Budget ($${Number(currentItem.budget).toLocaleString()}).`;
                    return 'No suggestion — enter a number or skip.';
                  })()}
                  value={draft.listed_price}
                  onChange={(v) => setDraft((d) => ({ ...d, listed_price: v }))}
                  type="number"
                />
              )}

              {/* ── Ordered Price + Receipt ── */}
              {currentItem._missing.includes('ordered_price') && (
                <FieldRow
                  label="Ordered price"
                  testIdBase="fillin-ordered"
                  hint="From the receipt. Leave blank to skip."
                  value={draft.ordered_price}
                  onChange={(v) => setDraft((d) => ({ ...d, ordered_price: v }))}
                  type="number"
                />
              )}

              {/* ── Status ── */}
              {currentItem._missing.includes('status') && (
                <div>
                  <Label className="text-xs">Status</Label>
                  <select
                    value={draft.status}
                    onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value }))}
                    className="mt-1 w-full h-9 px-2 rounded-md border bg-background text-sm"
                    data-testid="fillin-status-select"
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>{s ? s.charAt(0).toUpperCase() + s.slice(1) : '— skip —'}</option>
                    ))}
                  </select>
                  <p className="text-[10px] text-muted-foreground mt-1">Suggested: <strong>Delivered</strong>. Pick another or leave on "— skip —".</p>
                </div>
              )}

              {/* ── Receipt ── */}
              {currentItem._missing.includes('receipt') && (
                <div className="rounded-md border bg-muted/20 p-3 space-y-2">
                  <Label className="text-xs flex items-center gap-1.5">
                    <Paperclip className="h-3 w-3" /> Attach receipt
                  </Label>
                  {itemReceipts.length > 0 ? (
                    <p className="text-[11px] text-emerald-700">
                      ✓ {itemReceipts.length} receipt{itemReceipts.length === 1 ? '' : 's'} now linked.
                    </p>
                  ) : (
                    <p className="text-[11px] text-muted-foreground">No receipts on this item yet.</p>
                  )}
                  <input
                    ref={fileRef} type="file" accept="image/*,.pdf"
                    onChange={handleReceiptFile} className="hidden"
                    data-testid="fillin-receipt-file"
                  />
                  <Button
                    type="button" variant="outline" size="sm"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploadingReceipt}
                    className="h-7 text-xs gap-1"
                    data-testid="fillin-receipt-btn"
                  >
                    {uploadingReceipt ? <Loader2 className="h-3 w-3 animate-spin" /> : <Paperclip className="h-3 w-3" />}
                    {itemReceipts.length === 0 ? 'Choose file…' : 'Add another'}
                  </Button>
                </div>
              )}
            </div>

            <DialogFooter className="px-5 py-3 border-t bg-muted/20 flex flex-row items-center justify-between gap-2">
              <Button
                variant="ghost" size="sm" onClick={handleSaveAndExit}
                disabled={saving || uploadingReceipt}
                className="text-xs gap-1"
                data-testid="fillin-save-exit"
              >
                <Save className="h-3.5 w-3.5" /> Save &amp; exit
              </Button>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline" size="sm" onClick={handleSkipItem}
                  disabled={saving || uploadingReceipt}
                  className="text-xs gap-1"
                  data-testid="fillin-skip-item"
                >
                  <SkipForward className="h-3.5 w-3.5" /> Skip item
                </Button>
                <Button
                  size="sm" onClick={() => handleSaveItem({ andAdvance: true })}
                  disabled={saving || uploadingReceipt}
                  className="text-xs gap-1"
                  data-testid="fillin-save-next"
                >
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (isLast ? <CheckCircle2 className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />)}
                  {isLast ? 'Save & finish' : 'Save & next'}
                </Button>
              </div>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function FieldRow({ label, hint, value, onChange, type = 'text', testIdBase }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1"
        data-testid={`${testIdBase}-input`}
        placeholder={type === 'number' ? '0.00' : ''}
      />
      <p className="text-[10px] text-muted-foreground mt-1">{hint}</p>
    </div>
  );
}
